import { useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromKey = (k) => (k ? new Date(`${String(k).slice(0, 10)}T00:00:00`) : undefined);
const pretty = (k) => fromKey(k)?.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

const DAY_CLASSES = {
  caption_label: "hidden",
  caption_dropdowns: "flex items-center gap-1",
  dropdown: "cursor-pointer rounded-md border border-slate-200 bg-white px-1.5 py-1 font-heading text-sm font-semibold text-slate-800 hover:border-sky-300 focus:border-sky-400 focus:outline-none",
  vhidden: "sr-only",
  head_cell: "w-9 text-[11px] font-semibold uppercase text-slate-400",
  cell: "relative h-9 w-9 p-0 text-center text-sm",
  day: "h-9 w-9 rounded-lg p-0 text-sm font-normal text-slate-700 transition-colors hover:bg-sky-50 hover:text-sky-700 aria-selected:opacity-100",
  day_selected: "!bg-sky-600 !text-white font-semibold hover:!bg-sky-700",
  day_today: "font-bold text-sky-600 ring-1 ring-inset ring-sky-200",
  day_outside: "text-slate-300 opacity-60",
  nav_button: "inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800",
};

// Styled date picker; value/onChange use "YYYY-MM-DD" strings (null when cleared).
export function DatePicker({ value, onChange, testid, placeholder = "Select date", className, bare, label, monthHint }) {
  const [open, setOpen] = useState(false);
  const pick = (d) => { onChange(d ? toKey(d) : null); setOpen(false); };
  const now = new Date().getFullYear();
  const valid = (d) => (d && !isNaN(d) ? d : undefined);
  const val = valid(fromKey(value)), hint = valid(fromKey(monthHint));
  const years = [val, hint].filter(Boolean).map((d) => d.getFullYear());
  const fromYear = Math.min(now - 10, ...years), toYear = Math.max(now + 10, ...years);
  const start = val || hint;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} data-value={value || ""}
          className={cn("flex w-full items-center gap-2 text-left text-sm transition-colors",
            bare ? "bg-transparent" : "rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-sky-300 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100",
            className)}>
          {label && <span className="shrink-0 text-xs font-semibold text-slate-500">{label}</span>}
          <span className={cn("min-w-0 flex-1 truncate", value ? "text-slate-900" : "text-slate-400")}>{value ? pretty(value) : placeholder}</span>
          <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-xl border-slate-200 p-0 shadow-xl" data-testid={testid ? `${testid}-popover` : undefined}>
        <Calendar mode="single" selected={fromKey(value)} defaultMonth={start} onSelect={pick} weekStartsOn={1}
          captionLayout="dropdown-buttons" fromYear={fromYear} toYear={toYear}
          classNames={DAY_CLASSES} initialFocus />
        <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
          <button type="button" onClick={() => pick(new Date())} data-testid={testid ? `${testid}-today` : undefined}
            className="rounded-md px-2 py-1 text-xs font-semibold text-sky-600 hover:bg-sky-50">Today</button>
          {value && (
            <button type="button" onClick={() => pick(null)} data-testid={testid ? `${testid}-clear` : undefined}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
